/**
 * Auswertung der Lohnverrechnung (AP-22-E) — die Seite, die den Monatsexport
 * auslöst. Aufgebaut wie im Prototyp (`AuswertungPage.tsx`): Zeitraum in ganzen
 * Monaten, Mitarbeiterauswahl mit „alle", Variante der Dateiaufteilung, darunter
 * der Export.
 *
 * **Kein Listen-Standard** — die Seite listet nichts, sie stellt einen Auftrag
 * zusammen. FilterBar und Spaltenkonfiguration hätten hier nichts zu tun
 * (dieselbe Abgrenzung wie bei der Monatserfassung).
 *
 * Gerechnet und gebaut wird die Datei am Server; der Browser schickt nur, welcher
 * Zeitraum, welche Mitarbeiter und welche Variante gemeint sind.
 *
 * **Exportiert werden nur fertiggestellte Lohnmonate** (AP-22 Teil 2, G1). Die
 * Seite zeigt deshalb vor dem Export, was im gewählten Zeitraum fertig ist und
 * was noch offen — offene Monate fehlen in der Datei.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { erfuelltRecht } from '@symphenia/shared-types';
import { ExportButton } from '@/components/ui/ExportButton';
import { useAuthStore } from '@/lib/auth.store';
import { fieldLabel, inputBase, pageWide } from '@/lib/form-styles';
import { fetchPayrollEmployees, fetchPayrollMonthStates, monthsExportRequest, type LohnmonatStand } from '../api/payroll.api';
import { Hilfe } from '../components/Hilfe';

type Variante = 'ein-file' | 'datei-je-ma' | 'datei-je-monat';

const MONATSNAMEN = [
  'Jänner', 'Februar', 'März', 'April', 'Mai', 'Juni',
  'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember',
];

const VARIANTEN: { id: Variante; label: string; hinweis: string }[] = [
  {
    id: 'ein-file',
    label: 'Eine Datei',
    hinweis: 'Ein Arbeitsblatt je Mitarbeiter und Monat.',
  },
  {
    id: 'datei-je-ma',
    label: 'Eine Datei je Mitarbeiter',
    hinweis: 'Darin ein Blatt je Monat. Mehrere Dateien kommen als ZIP.',
  },
  {
    id: 'datei-je-monat',
    label: 'Eine Datei je Monat',
    hinweis: 'Darin ein Blatt je Mitarbeiter. Mehrere Dateien kommen als ZIP.',
  },
];

interface Mitarbeiter {
  id: string;
  email: string | null;
  isActive?: boolean;
  profile?: { firstName?: string | null; lastName?: string | null } | null;
}

function nameVon(u: Mitarbeiter): string {
  return [u.profile?.lastName, u.profile?.firstName].filter(Boolean).join(' ') || u.email || '—';
}

function heutigerMonat(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function monatText(monat: string): string {
  const [j, m] = monat.split('-').map(Number);
  return `${MONATSNAMEN[m - 1]} ${j}`;
}

/** Anzahl Monate im Zeitraum; 0 = Grenzen vertauscht. */
function monateZwischen(von: string, bis: string): number {
  const [vj, vm] = von.split('-').map(Number);
  const [bj, bm] = bis.split('-').map(Number);
  const diff = (bj - vj) * 12 + (bm - vm);
  return diff < 0 ? 0 : diff + 1;
}

export default function PayrollExportPage() {
  const authUser = useAuthStore((s) => s.user);
  const darfLesen = erfuelltRecht('payroll.day_entry.read', authUser?.permissions);
  const darfStand = erfuelltRecht('payroll.month.read', authUser?.permissions);
  const [staende, setStaende] = useState<LohnmonatStand[] | null>(null);

  const [von, setVon] = useState(heutigerMonat);
  const [bis, setBis] = useState(heutigerMonat);
  const [alleMitarbeiter, setAlleMitarbeiter] = useState<Mitarbeiter[]>([]);
  const [auchInaktive, setAuchInaktive] = useState(false);
  const [gewaehlt, setGewaehlt] = useState<string[]>([]);
  const [variante, setVariante] = useState<Variante>('ein-file');

  useEffect(() => {
    // AP-38-D: die eigene Mitarbeiterliste der Lohnverrechnung — ohne Externe
    // und ohne `core.user.read`.
    fetchPayrollEmployees(auchInaktive ? {} : { isActive: true })
      .then((liste0) => {
        const liste = liste0
          .filter((u) => !u.noPayroll)
          .slice()
          .sort((a, b) => nameVon(a).localeCompare(nameVon(b), 'de'));
        setAlleMitarbeiter(liste);
        const ids = new Set(liste.map((u) => u.id));
        setGewaehlt((prev) => prev.filter((id) => ids.has(id)));
      })
      .catch(() => setAlleMitarbeiter([]));
  }, [auchInaktive]);

  const monate = monateZwischen(von, bis);
  const alleGewaehlt = gewaehlt.length > 0 && gewaehlt.length === alleMitarbeiter.length;
  const jahr = new Date().getFullYear();

  const betreff = useMemo(() => {
    const zeitraum = monate > 1 ? `${monatText(von)} – ${monatText(bis)}` : monatText(von);
    return `Monatsauswertung ${zeitraum}`;
  }, [von, bis, monate]);

  // Stand der Lohnmonate im Zeitraum — nachgeladen bei jeder Änderung der Auswahl.
  useEffect(() => {
    if (!darfStand || monate === 0) {
      setStaende(null);
      return;
    }
    let aktuell = true;
    fetchPayrollMonthStates({ from: von, to: bis, userIds: gewaehlt, includeInactive: auchInaktive || undefined })
      .then((liste) => aktuell && setStaende(liste))
      .catch(() => aktuell && setStaende(null));
    return () => {
      aktuell = false;
    };
  }, [darfStand, von, bis, gewaehlt, auchInaktive, monate]);

  const fertige = staende?.filter((s) => s.status === 'FINAL') ?? [];
  const offene = staende?.filter((s) => s.status === 'OPEN') ?? [];
  // Ohne das Recht auf den Stand bleibt der Knopf da — der Server lehnt ab, wenn nichts fertig ist.
  const bereit = darfLesen && monate > 0 && (staende === null || fertige.length > 0);

  return (
    <div className={pageWide}>
      <div>
        <h1 className="text-3xl font-bold text-foreground">Auswertung</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Erzeugt die Monatsauswertung für die Lohnverrechnung — je Mitarbeiter und Monat ein Blatt mit einer
          Zeile je Tag, Summen und Aufschlüsselung. Der Zeitraum darf mehrere Monate umfassen.
        </p>
      </div>

      <div className="space-y-4 rounded-lg border border-border bg-card p-4 shadow-sm">
        <div className="flex flex-wrap items-end gap-4">
          <div className="space-y-1.5">
            <label className={fieldLabel} htmlFor="export-von">
              Von Monat
            </label>
            <input
              id="export-von"
              type="month"
              className={`${inputBase.replace('w-full ', '')} w-44`}
              value={von}
              onChange={(e) => e.target.value && setVon(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <label className={fieldLabel} htmlFor="export-bis">
              Bis Monat
            </label>
            <input
              id="export-bis"
              type="month"
              className={`${inputBase.replace('w-full ', '')} w-44`}
              value={bis}
              onChange={(e) => e.target.value && setBis(e.target.value)}
              aria-invalid={monate === 0 || undefined}
            />
            {monate === 0 && <p className="text-xs text-destructive">liegt vor dem Startmonat</p>}
          </div>
          <button
            type="button"
            className="mb-0.5 rounded-md border border-border px-3 py-2 text-sm text-foreground hover:bg-muted"
            onClick={() => {
              setVon(`${jahr}-01`);
              setBis(`${jahr}-12`);
            }}
          >
            Ganzes Jahr {jahr}
          </button>
          <p className="mb-2 text-sm text-muted-foreground">
            {monate > 0 ? `${monate} ${monate === 1 ? 'Monat' : 'Monate'}` : ''}
          </p>
        </div>
      </div>

      <div className="space-y-3 rounded-lg border border-border bg-card p-4 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-sm font-semibold text-foreground">
            Mitarbeiter{' '}
            <span className="font-normal text-muted-foreground">
              {gewaehlt.length === 0 ? '— ohne Auswahl werden alle exportiert' : `— ${gewaehlt.length} gewählt`}
            </span>
          </h2>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-sm text-muted-foreground">
              <input
                type="checkbox"
                className="h-4 w-4 accent-[hsl(var(--primary))]"
                checked={auchInaktive}
                onChange={(e) => setAuchInaktive(e.target.checked)}
              />
              auch nicht mehr aktive
            </label>
            <button
              type="button"
              className="text-sm text-primary underline"
              onClick={() => setGewaehlt(alleGewaehlt ? [] : alleMitarbeiter.map((u) => u.id))}
            >
              {alleGewaehlt ? 'Auswahl aufheben' : 'alle wählen'}
            </button>
          </div>
        </div>
        <div className="grid gap-x-6 gap-y-1.5 sm:grid-cols-2 lg:grid-cols-3">
          {alleMitarbeiter.map((u) => (
            <label key={u.id} className="flex items-center gap-2 text-sm text-foreground">
              <input
                type="checkbox"
                className="h-4 w-4 accent-[hsl(var(--primary))]"
                checked={gewaehlt.includes(u.id)}
                onChange={(e) =>
                  setGewaehlt((prev) => (e.target.checked ? [...prev, u.id] : prev.filter((id) => id !== u.id)))
                }
              />
              {nameVon(u)}
            </label>
          ))}
          {alleMitarbeiter.length === 0 && (
            <p className="text-sm text-muted-foreground">Keine Mitarbeiter gefunden.</p>
          )}
        </div>
      </div>

      <div className="space-y-3 rounded-lg border border-border bg-card p-4 shadow-sm">
        <h2 className="inline-flex items-center gap-1.5 text-sm font-semibold text-foreground">
          Aufteilung der Dateien
          <Hilfe>
            Gilt für die Excel-Mappe. PDF und Word sind immer ein Dokument mit einer Seite je Mitarbeiter und
            Monat.
          </Hilfe>
        </h2>
        <div className="space-y-2">
          {VARIANTEN.map((v) => (
            <label key={v.id} className="flex items-start gap-2 text-sm">
              <input
                type="radio"
                name="variante"
                className="mt-0.5 h-4 w-4 accent-[hsl(var(--primary))]"
                checked={variante === v.id}
                onChange={() => setVariante(v.id)}
              />
              <span>
                <span className="text-foreground">{v.label}</span>
                <span className="block text-xs text-muted-foreground">{v.hinweis}</span>
              </span>
            </label>
          ))}
        </div>
      </div>

      {staende && (
        <div className="space-y-2 rounded-lg border border-border bg-card p-4 shadow-sm">
          <h2 className="text-sm font-semibold text-foreground">
            Fertiggestellt{' '}
            <span className="font-normal text-muted-foreground">
              — {fertige.length} {fertige.length === 1 ? 'Monat wird' : 'Monate werden'} exportiert
              {offene.length > 0 ? `, ${offene.length} noch offen` : ''}
            </span>
          </h2>
          {staende.length === 0 && (
            <p className="text-sm text-muted-foreground">Im gewählten Zeitraum ist nichts erfasst.</p>
          )}
          {offene.length > 0 && (
            <>
              <p className="text-sm text-muted-foreground">
                Offene Monate fehlen in der Datei. Stelle sie in der Monatserfassung fertig:
              </p>
              <ul className="grid gap-x-6 gap-y-1 text-sm sm:grid-cols-2 lg:grid-cols-3">
                {offene.map((s) => (
                  <li key={`${s.userId}:${s.jahr}-${s.monat}`}>
                    <Link
                      to={`/payroll/entry?user=${s.userId}&month=${s.jahr}-${String(s.monat).padStart(2, '0')}`}
                      className="text-primary hover:underline"
                    >
                      {s.name}, {MONATSNAMEN[s.monat - 1]} {s.jahr}
                    </Link>{' '}
                    <span className="text-muted-foreground">
                      ({s.tage} {s.tage === 1 ? 'Tag' : 'Tage'} erfasst)
                    </span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3">
        {bereit ? (
          <ExportButton
            defaultFilename="Zeitauswertung"
            showOrientation={false}
            request={(format) =>
              monthsExportRequest({
                from: von,
                to: bis,
                userIds: gewaehlt,
                includeInactive: auchInaktive || undefined,
                variante,
                format,
              })
            }
            mail={{ source: 'EXPORT_PAYROLL_MONTHLY', title: 'Monatsauswertung', subject: betreff }}
          />
        ) : (
          <p className="text-sm text-muted-foreground">
            {!darfLesen
              ? 'Dir fehlt das Recht, Monate zu lesen.'
              : monate === 0
                ? 'Der Zeitraum ist leer.'
                : 'Im gewählten Zeitraum ist kein Monat fertiggestellt.'}
          </p>
        )}
        <Link to="/payroll/entry" className="text-sm text-muted-foreground hover:text-foreground hover:underline">
          Zur Monatserfassung
        </Link>
      </div>
    </div>
  );
}
